import { useEffect, useRef } from 'react'

/**
 * Puts focus on the board when a round starts.
 *
 * The games listen for keys on the window, so they play perfectly well with
 * focus sitting on nothing. Two things go wrong if it is left there. A screen
 * reader is given no reason to say a new round has begun — she asked for
 * another word and heard silence. And the next Tab starts from the top of the
 * page again, which on a television means walking back down through the header
 * every single round.
 *
 * The container is focusable only programmatically (`tabIndex={-1}`), so this
 * adds nothing to the tab order — it only moves the starting point to where
 * the game actually is.
 *
 * Its own focus ring is suppressed at every call site (`outline-none`). The
 * board is a place to start tabbing from, not a control, and drawing a ring
 * around the whole card meant Sky's Memory Match sometimes showed two: one
 * round the board and one round the card the cursor was on. The ring belongs
 * on the thing being pointed at.
 */
export function useRoundFocus<Element extends HTMLElement>(seed: string, finished = false) {
  const ref = useRef<Element>(null)

  useEffect(() => {
    /*
     * Focus follows the round, not the mount: asking for another word keeps
     * the same component and only changes the seed.
     *
     * Without moving the screen, though. Focusing an element makes a browser
     * scroll it into view, and the board here is the whole round card — 812px
     * of it on a television, where the scroll region is 881px and the game's
     * title sits above it. Making all of the card visible therefore meant
     * scrolling down 91px and pushing the top of the screen out of sight, so
     * Rocky's opened with his progress line and his race already gone. The
     * browser was doing exactly what "make this visible" means; the card is
     * just not the thing that needs to be.
     *
     * Nothing is lost by staying put: a round starts at the top of its screen
     * because that is where the screen already is, and every game fits it.
     */
    ref.current?.focus({ preventScroll: true })
  }, [seed])

  /*
   * And back to the board when the round ends, which is not tidiness.
   *
   * The cursor games put real focus on the square it is pointing at, so that
   * the ring and the browser's focus are the same thing. That leaves a
   * `<button>` focused at the moment the round finishes — and `useGameKeys`
   * refuses Enter and Space while a button has focus, exactly as CLAUDE.md
   * requires, so the game's own "Enter plays again" never runs. The browser
   * activates the focused square instead, which by then does nothing at all.
   *
   * The result was a television telling her to **press Enter to play again**
   * under a key that did nothing: she had to leave the game and come back in.
   * Reported on Maritza's Mosaic; Stripey's Sudoku had it too, since DOL-44.
   *
   * Handing focus back here rather than exempting the keys in `useGameKeys`,
   * because the rule there is right — Enter belongs to a focused control. What
   * was wrong is that a square stayed focused after it had stopped being
   * something to press. The finish is also the moment the screen changes
   * wholesale, so it is where focus should start again anyway.
   */
  useEffect(() => {
    if (!finished) return

    ref.current?.focus({ preventScroll: true })
  }, [finished])

  return ref
}
